import React, { useState } from "react";
import { UpArrowCardIcon, DownArrowCardIcon, YesIcon, NoIcon } from "../../assets/Icons";
import Button from "../../components/common/button/button";
import { useTranslation } from "../../contexts/TranslationContext";

export const KaratHelpAccordion: React.FC = () => {
  const [open, setOpen] = useState(false);
  const t = useTranslation();
  return (
    <div className={`weight-slider__help karat-accordion${open ? " open" : ""}`}>
      <button className="weight-slider__help-header" onClick={() => setOpen((p) => !p)}>
        <span>{t.gold.needHelp}</span>
        {open ? <UpArrowCardIcon /> : <DownArrowCardIcon />}
      </button>
      {open && (
        <div className="weight-slider__help-content">
          <p>{t.gold.karatHelp}</p>
          <p>On <strong>{t.gold.karatRingsBold}</strong>{t.gold.karatRingsRest}</p>
          <p>On <strong>{t.gold.karatNecklacesBold}</strong>{t.gold.karatNecklacesRest}</p>
          <p>On <strong>{t.gold.karatEarringsBold}</strong>{t.gold.karatEarringsRest}</p>
        </div>
      )}
    </div>
  );
};

export const PurityHelpAccordion: React.FC<{ metalName: string }> = ({ metalName }) => {
  const [open, setOpen] = useState(false);
  const t = useTranslation();
  const isSilver = metalName === "Silver";
  return (
    <div className={`weight-slider__help karat-accordion${open ? " open" : ""}`}>
      <button className="weight-slider__help-header" onClick={() => setOpen((p) => !p)}>
        <span>{t.gold.needHelp}</span>
        {open ? <UpArrowCardIcon /> : <DownArrowCardIcon />}
      </button>
      {open && (
        <div className="weight-slider__help-content">
          {isSilver ? (
            <>
              <p>{t.gold.purityHelp}</p>
              <div className="weight-slider__help-purity-list">
                <p><strong>99.9%(999)</strong>{t.gold.alsoKnownAs}<strong>Fine silver</strong></p>
                <p><strong>92.5%(925)</strong>{t.gold.alsoKnownAs}<strong>Sterling silver</strong></p>
                <p><strong>90%(900)</strong></p>
                <p><strong>80%(800)</strong></p>
              </div>
            </>
          ) : (
            <>
              <p>{t.gold.purityHelp}</p>
              <div className="weight-slider__help-purity-list">
                <p><strong>95%(950)</strong>{t.gold.or}<strong>PT950</strong></p>
                <p><strong>90%(900)</strong>{t.gold.or}<strong>PT900</strong></p>
                <p><strong>85%(850)</strong>{t.gold.or}<strong>PT850</strong></p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

type GoldTextAreaProps = {
  initialValue: string;
  initialBrandedValue?: string;
  showBrandedQuestion?: boolean;
  onContinue: (text: string, isBrandedOrDesigner: string) => void;
};

export const GoldTextAreaAttribute: React.FC<GoldTextAreaProps> = ({
  initialValue,
  initialBrandedValue = "",
  showBrandedQuestion = false,
  onContinue,
}) => {
  const [text, setText] = useState(initialValue);
  const [isBranded, setIsBranded] = useState(initialBrandedValue);
  const t = useTranslation();
  const MAX_CHARS = 500;
  return (
    <div className="gold-textarea-wrapper">
      {showBrandedQuestion && (
        <>
          <div className="gold-yesno-list">
            {(["Yes", "No"] as const).map((v) => {
              const isYes = v === "Yes";
              const isSelected = isBranded.toLowerCase() === v.toLowerCase();

              // Figma nests the specialist-review note inside the Yes card
              // itself (same bordered/shadowed container), not as a block
              // below both cards — only swap structure for that one state.
              if (isYes && isSelected) {
                return (
                  <div key={v} className="gold-yesno-card gold-yesno-card-with-message">
                    <button
                      type="button"
                      className="gold-yesno-card-row"
                      onClick={() => setIsBranded(v)}
                    >
                      <span className="gold-yesno-icon yes">
                        <YesIcon width={44} height={44} />
                      </span>
                      <span className="gold-yesno-label">{v}</span>
                    </button>
                    <div className="gold-branded-info-message">
                      <svg width="20" height="20" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M12.0003 17.4167C12.2129 17.4167 12.391 17.3448 12.5345 17.201C12.6782 17.0573 12.75 16.8792 12.75 16.6667V12.4167C12.75 12.2042 12.6781 12.0261 12.5343 11.8822C12.3904 11.7386 12.2122 11.6667 11.9998 11.6667C11.7871 11.6667 11.609 11.7386 11.4655 11.8822C11.3218 12.0261 11.25 12.2042 11.25 12.4167V16.6667C11.25 16.8792 11.3219 17.0573 11.4658 17.201C11.6096 17.3448 11.7878 17.4167 12.0003 17.4167ZM12 9.95525C12.2288 9.95525 12.4207 9.87783 12.5755 9.723C12.7303 9.56816 12.8077 9.37633 12.8077 9.1475C12.8077 8.91866 12.7303 8.72683 12.5755 8.572C12.4207 8.41733 12.2288 8.34 12 8.34C11.7712 8.34 11.5793 8.41733 11.4245 8.572C11.2697 8.72683 11.1923 8.91866 11.1923 9.1475C11.1923 9.37633 11.2697 9.56816 11.4245 9.723C11.5793 9.87783 11.7712 9.95525 12 9.95525ZM12.0017 22.1667C10.6877 22.1667 9.45267 21.9174 8.2965 21.4187C7.14033 20.9201 6.13467 20.2433 5.2795 19.3885C4.42433 18.5337 3.74725 17.5284 3.24825 16.3727C2.74942 15.2171 2.5 13.9823 2.5 12.6685C2.5 11.3545 2.74933 10.1194 3.248 8.96325C3.74667 7.80708 4.42342 6.80141 5.27825 5.94625C6.13308 5.09108 7.13833 4.414 8.294 3.915C9.44967 3.41616 10.6844 3.16675 11.9983 3.16675C13.3123 3.16675 14.5473 3.41608 15.7035 3.91475C16.8597 4.41341 17.8653 5.09016 18.7205 5.945C19.5757 6.79983 20.2528 7.80508 20.7518 8.96075C21.2506 10.1164 21.5 11.3512 21.5 12.665C21.5 13.979 21.2507 15.2141 20.752 16.3702C20.2533 17.5264 19.5766 18.5321 18.7218 19.3872C17.8669 20.2424 16.8617 20.9195 15.706 21.4185C14.5503 21.9173 13.3156 22.1667 12.0017 22.1667ZM12 20.6667C14.2333 20.6667 16.125 19.8917 17.675 18.3417C19.225 16.7917 20 14.9001 20 12.6667C20 10.4334 19.225 8.54175 17.675 6.99175C16.125 5.44175 14.2333 4.66675 12 4.66675C9.76667 4.66675 7.875 5.44175 6.325 6.99175C4.775 8.54175 4 10.4334 4 12.6667C4 14.9001 4.775 16.7917 6.325 18.3417C7.875 19.8917 9.76667 20.6667 12 20.6667Z" fill="#1D40AE"/>
                      </svg>
                      <span>{t.gold.brandedSpecialistNote}</span>
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={v}
                  type="button"
                  className={`gold-yesno-card${isSelected ? " selected" : ""}`}
                  onClick={() => setIsBranded(v)}
                >
                  <span className={`gold-yesno-icon${isYes ? " yes" : " no"}`}>
                    {isYes ? <YesIcon width={44} height={44} /> : <NoIcon width={44} height={44} />}
                  </span>
                  <span className="gold-yesno-label">{v}</span>
                </button>
              );
            })}
          </div>
          <div className="gold-textarea-label">
            <div className="attribute-name">{t.headers.goldDetailsTitle}</div>
            <div className="gold-textarea-hint">{t.headers.goldDetailsSubtitle}</div>
          </div>
        </>
      )}
      <textarea
        className="gold-textarea"
        placeholder={t.gold.additionalComments}
        value={text}
        onChange={(e) => setText(e.target.value.replace(/\p{Extended_Pictographic}/gu, '').slice(0, MAX_CHARS))}
        rows={5}
      />
      <div className="gold-textarea__counter">{text.length}/{MAX_CHARS}</div>
      <div className="continue-button-container-gold">
        <Button
          label={t.shared.continue}
          onClick={() => onContinue(text.trim(), isBranded)}
          styleType="primary"
          disabled={showBrandedQuestion && !isBranded}
        />
      </div>
    </div>
  );
};
